import dayjs from '@/config/dayjs';

/**
 * Get today's date in school timezone
 * This is the client-side equivalent of backend's getTodayForSchool()
 *
 * @param timezone School timezone (e.g., "America/Denver")
 * @returns Today's date in YYYY-MM-DD format
 */
export const getTodayInSchoolTZ = (timezone: string): string => {
  return dayjs().tz(timezone).format('YYYY-MM-DD');
};

/**
 * Formats a timestamp in the school's timezone
 * @param timestamp ISO 8601 UTC timestamp from backend
 * @param timezone School timezone (e.g., "America/Denver")
 * @param format DayJS format string (default: 'MMM D, YYYY h:mm A')
 * @param locale Optional dayjs locale for localized month/day names (e.g., 'es')
 * @returns Formatted date string in school timezone
 */
export const formatTimestampInSchoolTZ = (
  timestamp: string | Date | undefined,
  timezone: string,
  format: string = 'MMM D, YYYY h:mm A',
  locale?: string,
): string => {
  if (!timestamp) return 'Not Set';

  let d = dayjs(timestamp).tz(timezone);
  if (locale) d = d.locale(locale);
  return d.format(format);
};

/**
 * Formats a date (no time component)
 * @param dateString YYYY-MM-DD format
 * @param format DayJS format string (default: 'MMM D, YYYY')
 * @returns Formatted date string
 */
export const formatDate = (dateString: string | undefined, format: string = 'MMM D, YYYY'): string => {
  if (!dateString) return 'Not Set';

  return dayjs(dateString).format(format);
};

/**
 * Gets relative time (e.g., "2 hours ago") in school timezone
 * @param timestamp ISO 8601 UTC timestamp
 * @param timezone School timezone
 * @returns Relative time string
 */
export const getRelativeTime = (timestamp: string | Date, timezone: string): string => {
  return dayjs(timestamp).tz(timezone).fromNow();
};

/**
 * Converts 24-hour time format to 12-hour format with AM/PM
 * @param time24 Time string in HH:MM or HH:MM:SS format
 * @returns Time string in 12-hour format (e.g., "8:30 AM", "2:30 PM")
 */
export const formatTime12Hour = (time24: string): string => {
  // Parse the time string (handles both HH:MM:SS and HH:MM)
  const [hoursStr, minutesStr] = time24.split(':');
  let hours = parseInt(hoursStr, 10);
  const minutes = minutesStr;

  // Determine AM/PM
  const period = hours >= 12 ? 'PM' : 'AM';

  // Convert to 12-hour format
  if (hours === 0) {
    hours = 12; // Midnight
  } else if (hours > 12) {
    hours = hours - 12;
  }

  // Return formatted time (no leading zero on hours)
  return `${hours}:${minutes} ${period}`;
};
